import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { onboardingAiActions } from "../store/onboarding-ai.slice";
import {
  fetchGetAiStore,
  fetchMergeAiStore,
  fetchRegenerateAiStore,
} from "../services/onboarding-ai.service";
import { onboardingAiTextSelector } from "../store/onboarding-ai.selector";
import { useLocalStorage } from "@domun/component-library";
import { useNotification } from "@dashboard/hooks/use-notification.hook";
import { useNavigate } from "react-router-dom";

/** Vista previa del invitado, para recuperarla al recargar. */
export const GUEST_PREVIEW_KEY = "onboardingAiGuestPreview";

export const useOnboardingAi = () => {
  const [showLoader, setShowLoader] = useState(false);
  const [token] = useLocalStorage("accessToken", "");
  const dispatch = useDispatch();
  const aiText = useSelector(onboardingAiTextSelector);
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getCatalogueInfo = () => {
    setShowLoader(true);
    dispatch(onboardingAiActions.setEmptyState(true));
    dispatch(onboardingAiActions.setLoadIframe(false));
    if (sessionStorage.getItem("guestToken")) {
      regenerateCatalogueInfo();
    } else {
      generateCatalogueInfo();
    }
  };

  const generateCatalogueInfo = () => {
    fetchGetAiStore(aiText)
      .then((response) => {
        dispatch(onboardingAiActions.setLoadIframe(true));
        sessionStorage.setItem("guestToken", response.accessToken);
        const previewUrl = `${response.url}&embedded=true&noEvents=true`;
        // Para volver a la vista previa si recarga antes de registrarse.
        sessionStorage.setItem(GUEST_PREVIEW_KEY, JSON.stringify({ url: previewUrl, prompt: aiText }));
        dispatch(onboardingAiActions.setUrl(previewUrl));
        setTimeout(() => {
          dispatch(onboardingAiActions.setEmptyState(false));
          setShowLoader(false);
        }, 2000);
      })
      .catch((error) => {
        setShowLoader(false);
        handleErrorMessage(error.response?.status || error.status);
      });
  };

  const regenerateCatalogueInfo = () => {
    setShowLoader(true);
    fetchRegenerateAiStore(aiText, sessionStorage.getItem("guestToken") || "")
      .then((response) => {
        dispatch(onboardingAiActions.setLoadIframe(true));
        const previewUrl = `${response.url}&embedded=true&noEvents=true`;
        sessionStorage.setItem(GUEST_PREVIEW_KEY, JSON.stringify({ url: previewUrl, prompt: aiText }));
        dispatch(onboardingAiActions.setUrl(previewUrl));
        setTimeout(() => {
          dispatch(onboardingAiActions.setEmptyState(false));
          setShowLoader(false);
        }, 2000);
      })
      .catch((error) => {
        setShowLoader(false);
        handleErrorMessage(error.response?.status || error.status);
      });
  };

  const mergeUserGuest = () => {
    fetchMergeAiStore(token || "", sessionStorage.getItem("guestToken") || "")
      .then(() => {})
      .catch((error) => handleErrorMessage(error.response?.status || error.status));
    sessionStorage.removeItem("guestToken");
  };

  const handleErrorMessage = (status?: number) => {
    const message = status === 504 
      ? t("onboardingAi.errors.timeout") 
      : t("onboardingAi.errors.generic");
    
    notify({ message, type: "error" });
    
    if (status === 401) {
      setTimeout(() => navigate(0), 2000);
    }
  };

  return {
    showLoader,
    setShowLoader,
    getCatalogueInfo,
    mergeUserGuest,
  };
};
